const AUTH_STATUS_VALUES = ["idle", "email-sent", "verified"];
const USER_MODE_VALUES = ["new", "existing"];
const HANDOFF_TOKEN_KEY = "mincool.onboardingHandoffToken";
const CURRENT_PAGE_HANDOFF_TOKEN_KEY = "__mincoolOnboardingHandoffTokenFromUrl";
const HANDOFF_QUERY_PARAM_KEY = "handoff";
const AUTH_DRAFT_KEY_PREFIX = "mincool.onboardingAuthDraft";
const AUTH_RETURN_QUERY_PARAM_KEY = "returnTo";

const DEFAULT_AUTH_STATE = Object.freeze({
  status: "idle",
  email: "",
  firstName: "",
  lastName: "",
  passwordDraft: "",
  passwordSubmittedDuringSignup: false,
  userMode: "new",
  languageStepComplete: false,
});

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{1,}$/.test(String(value || "").trim());
}

export function normalizeOnboardingEmailAddress(value = "") { return String(value || "").trim(); }

function createTimeoutError(timeoutMs) {
  const error = new Error(`Email lookup timed out after ${timeoutMs}ms.`);
  error.name = "TimeoutError";
  return error;
}

function withTimeout(promise, timeoutMs) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return Promise.resolve(promise);
  }

  const timerHost = typeof window !== "undefined" ? window : globalThis;
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeoutId = timerHost.setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      reject(createTimeoutError(timeoutMs));
    }, timeoutMs);

    Promise.resolve(promise).then(
      (value) => {
        if (settled) {
          return;
        }
        settled = true;
        timerHost.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        if (settled) {
          return;
        }
        settled = true;
        timerHost.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

function normalizeStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return AUTH_STATUS_VALUES.includes(normalized) ? normalized : DEFAULT_AUTH_STATE.status;
}

function normalizeUserMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return USER_MODE_VALUES.includes(normalized) ? normalized : DEFAULT_AUTH_STATE.userMode;
}

export function normalizeOnboardingAuthState(partial = {}) {
  return {
    ...DEFAULT_AUTH_STATE,
    ...partial,
    status: normalizeStatus(partial.status),
    email: normalizeOnboardingEmailAddress(partial.email),
    firstName: String(partial.firstName || ""),
    lastName: String(partial.lastName || ""),
    passwordDraft: String(partial.passwordDraft || ""),
    passwordSubmittedDuringSignup: partial.passwordSubmittedDuringSignup === true,
    userMode: normalizeUserMode(partial.userMode),
    languageStepComplete: partial.languageStepComplete === true,
  };
}

export function buildInitialOnboardingAuthState({
  storedAuth = {},
  storedAuthDraft = {},
  isolateStoredAuth = false,
} = {}) {
  return normalizeOnboardingAuthState({
    ...(isolateStoredAuth ? {} : storedAuth),
    ...storedAuthDraft,
  });
}

export function buildRetryOnboardingAuthState(partial = {}) {
  return normalizeOnboardingAuthState({ ...partial, status: "idle", email: "", passwordSubmittedDuringSignup: false });
}

export function shouldUpdateVerifiedSignupPassword({ passwordDraft = "", passwordSubmittedDuringSignup = false } = {}) {
  return String(passwordDraft || "").trim().length > 0 && passwordSubmittedDuringSignup !== true;
}

export function normalizeOnboardingEmailCode(value = "") {
  return String(value || "").replace(/\D/g, "").slice(0, 6);
}

export function getOnboardingEmailCodeVerifyTypes(userMode = "", options = {}) {
  const normalizedUserMode = normalizeUserMode(userMode);
  const linkedContext = options && typeof options === "object"
    ? options.linkedContext === true
    : false;

  if (normalizedUserMode === "existing") {
    return ["email", "magiclink"];
  }

  if (linkedContext) {
    return ["email_change", "email", "signup"];
  }

  return ["email", "signup", "email_change"];
}

export async function resolveOnboardingEmailUserMode(email = "", options = {}) {
  const normalizedEmail = normalizeOnboardingEmailAddress(email);
  const fallbackUserMode = normalizeUserMode(options.fallbackUserMode);
  const loadEmailStatus = typeof options.loadEmailStatus === "function"
    ? options.loadEmailStatus
    : null;
  const timeoutMs = Number.isFinite(options.timeoutMs) ? Number(options.timeoutMs) : 4_000;
  const retries = Number.isFinite(options.retries) ? Math.max(0, Number(options.retries)) : 1;

  if (!normalizedEmail || !loadEmailStatus) {
    return fallbackUserMode;
  }

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const payload = await withTimeout(loadEmailStatus(normalizedEmail), timeoutMs);
      return payload?.exists ? "existing" : "new";
    } catch {
      // Retry bounded email lookups so the auth form cannot stay unresolved indefinitely.
    }
  }

  return fallbackUserMode;
}

export function shouldRetryOnboardingAuthError(error) {
  const message = error instanceof Error ? String(error.message || "").trim() : "";
  if (!message || message === "{}") {
    return true;
  }
  return /failed to fetch|load failed|network\s?error|network request failed/i.test(message);
}

export async function retryOnboardingAuthOperation(operation, options = {}) {
  const attemptCount = Number.isFinite(options.attempts) ? Math.max(1, Number(options.attempts)) : 3;
  const baseDelayMs = Number.isFinite(options.baseDelayMs) ? Math.max(0, Number(options.baseDelayMs)) : 300;
  const timerHost = typeof window !== "undefined" ? window : globalThis;
  let lastError = null;

  for (let attempt = 1; attempt <= attemptCount; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt === attemptCount || !shouldRetryOnboardingAuthError(error)) {
        throw error;
      }
      await new Promise((resolve) => timerHost.setTimeout(resolve, baseDelayMs * attempt));
    }
  }

  throw lastError || new Error("We hit a problem. Please try again.");
}

export function getOnboardingAuthUiState({
  email = "",
  firstName = "",
  lastName = "",
  passwordDraft = "",
  userMode = "",
  checkingEmail = false,
  forceShowPasswordField = false,
} = {}) {
  const emailValid = isValidEmail(email);
  const normalizedUserMode = USER_MODE_VALUES.includes(String(userMode || "").trim().toLowerCase())
    ? String(userMode || "").trim().toLowerCase()
    : "";
  const namesComplete = String(firstName || "").trim().length > 0 && String(lastName || "").trim().length > 0;
  const showNameFields = emailValid && !checkingEmail && normalizedUserMode === "new";
  const showPasswordField = forceShowPasswordField || (
    emailValid
    && !checkingEmail
    && (normalizedUserMode === "existing" || (normalizedUserMode === "new" && namesComplete))
  );
  const canSubmit = !emailValid
    ? false
    : normalizedUserMode === "existing"
      ? true
      : normalizedUserMode === "new"
        ? namesComplete
        : true;

  let primaryLabel = "Continue";
  if (normalizedUserMode === "existing") {
    primaryLabel = String(passwordDraft || "").trim() ? "Sign in" : "Send magic link";
  } else if (normalizedUserMode === "new") {
    primaryLabel = String(passwordDraft || "").trim() ? "Create account" : "Send verification email";
  }

  return {
    emailValid,
    namesComplete,
    showNameFields,
    showPasswordField,
    canSubmit,
    primaryLabel,
    resolvedUserMode: normalizedUserMode,
  };
}

export function getOnboardingAuthFieldProps(userMode = "", options = {}) {
  const requestedUserMode = String(userMode || "").trim().toLowerCase();
  const fallbackUserMode = String(options.unresolvedUserMode || "").trim().toLowerCase() === "existing"
    ? "existing"
    : "new";
  const resolvedUserMode = USER_MODE_VALUES.includes(requestedUserMode)
    ? requestedUserMode
    : fallbackUserMode;

  return {
    email: {
      autoCapitalize: "none",
      autoComplete: resolvedUserMode === "existing" ? "username" : "email",
      autoCorrect: "on",
      inputMode: "email",
      name: "email",
      type: "text",
    },
    firstName: {
      autoCapitalize: "words",
      autoComplete: "given-name",
      name: "first-name",
    },
    lastName: {
      autoCapitalize: "words",
      autoComplete: "family-name",
      name: "last-name",
    },
    password: {
      autoCapitalize: "none",
      autoComplete: resolvedUserMode === "existing" ? "current-password" : "new-password",
      autoCorrect: "off",
      name: resolvedUserMode === "existing" ? "current-password" : "new-password",
      placeholder: resolvedUserMode === "existing" ? "Password (optional)" : "Set a password (optional)",
      spellCheck: false,
    },
    emailCode: {
      autoCapitalize: "none",
      autoComplete: "one-time-code",
      autoCorrect: "off",
      enterKeyHint: "done",
      id: "one-time-code",
      inputMode: "numeric",
      name: "one-time-code",
      pattern: "[0-9]*",
      spellCheck: false,
      type: "text",
    },
  };
}

export function isVerifiedSupabaseEmailUser(user, expectedEmail = "") {
  if (!user) {
    return false;
  }

  const normalizedExpectedEmail = String(expectedEmail || "").trim().toLowerCase();
  const normalizedUserEmail = String(user.email || "").trim().toLowerCase();

  if (!normalizedUserEmail) {
    return false;
  }

  if (normalizedExpectedEmail && normalizedUserEmail !== normalizedExpectedEmail) {
    return false;
  }

  return Boolean(user.email_confirmed_at || user.confirmed_at);
}

export function isMatchingSupabaseEmailUser(user, expectedEmail = "") {
  if (!user) {
    return false;
  }

  const normalizedExpectedEmail = String(expectedEmail || "").trim().toLowerCase();
  const normalizedUserEmail = String(user.email || "").trim().toLowerCase();

  if (!normalizedExpectedEmail) {
    return Boolean(normalizedUserEmail);
  }

  return normalizedUserEmail === normalizedExpectedEmail;
}

export function isAuthenticatedSupabaseUser(user) {
  if (!user) {
    return false;
  }
  return !Boolean(user.is_anonymous)
    && String(user.id || "").trim().length > 0
    && (isVerifiedSupabaseEmailUser(user) || Boolean(String(user.phone || "").trim() && (user.phone_confirmed_at || (!user.email && user.confirmed_at))));
}

export function getPreferredSupabaseUser({ fetchedUser = null, sessionUser = null } = {}) {
  if (fetchedUser) {
    return fetchedUser;
  }
  return sessionUser || null;
}

export async function resolveAuthenticatedSupabaseUser(client, session = null) {
  if (!client || !session?.access_token) {
    return null;
  }

  const sessionUser = session?.user || null;
  if (isAuthenticatedSupabaseUser(sessionUser)) {
    return sessionUser;
  }

  const {
    data: userPayload,
    error: userError,
  } = await client.auth.getUser();
  if (userError && !sessionUser) {
    throw userError;
  }

  const user = getPreferredSupabaseUser({
    fetchedUser: userPayload?.user || null,
    sessionUser,
  });

  return isAuthenticatedSupabaseUser(user) ? user : null;
}

export function readOnboardingHandoffTokenFromSearch(search = "") {
  try {
    return String(buildSearchParams(search).get(HANDOFF_QUERY_PARAM_KEY) || "").trim();
  } catch {
    return "";
  }
}

function readOnboardingHandoffTokenFromUrl() {
  if (typeof window === "undefined" || !window.location?.search) {
    return null;
  }
  return readOnboardingHandoffTokenFromSearch(window.location.search) || null;
}

function buildSearchParams(search = "") {
  return new URLSearchParams(String(search || "").replace(/^\?/, ""));
}

function buildTokenFingerprintContextKey(prefix = "", token = "") {
  const normalizedPrefix = String(prefix || "").trim();
  const normalized = String(token || "").trim();
  if (!normalized) {
    return `${normalizedPrefix}.missing`;
  }

  let hash = 0x811c9dc5;
  let reverseHash = 0x9e3779b9;
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;

    reverseHash ^= normalized.charCodeAt(normalized.length - index - 1);
    reverseHash = Math.imul(reverseHash, 0x85ebca6b) >>> 0;
  }

  return [
    normalizedPrefix,
    normalized.length.toString(36),
    hash.toString(36),
    reverseHash.toString(36),
  ].join(".");
}

export function buildSupportEmailClaimAuthDraftContextKey(token = "") {
  return buildTokenFingerprintContextKey("success.claim", token);
}

export function buildOnboardingHandoffAuthDraftContextKey(token = "") {
  return buildTokenFingerprintContextKey("success.handoff", token);
}

export function resolveOnboardingAuthDraftContextKey({
  isSignInSurface = false,
  supportEmailClaimToken = "",
  supportEmailClaimHandoffToken = "",
  supportEmailClaimCallback = false,
  linkedHandoffToken = "",
  currentAccountId = "",
} = {}) {
  if (isSignInSurface) {
    return "signin";
  }

  const normalizedClaimToken = String(supportEmailClaimToken || "").trim();
  const normalizedHandoffToken = String(supportEmailClaimHandoffToken || "").trim();
  if (normalizedHandoffToken && (normalizedClaimToken || supportEmailClaimCallback)) {
    return buildOnboardingHandoffAuthDraftContextKey(normalizedHandoffToken);
  }

  if (normalizedClaimToken) {
    return buildSupportEmailClaimAuthDraftContextKey(normalizedClaimToken);
  }

  const normalizedLinkedHandoffToken = String(linkedHandoffToken || "").trim();
  if (normalizedLinkedHandoffToken) {
    return buildOnboardingHandoffAuthDraftContextKey(normalizedLinkedHandoffToken);
  }

  return `success.${String(currentAccountId || "unlinked").trim() || "unlinked"}`;
}

export function normalizeAuthReturnPath(value = "") {
  const normalized = String(value || "").trim();
  if (!normalized || !normalized.startsWith("/") || normalized.startsWith("//")) {
    return "";
  }
  return normalized;
}

export function readAuthReturnPathFromSearch(search = "") {
  return normalizeAuthReturnPath(buildSearchParams(search).get(AUTH_RETURN_QUERY_PARAM_KEY));
}

export function withAuthReturnPath(path = "/auth", returnPath = "") {
  const normalizedPath = String(path || "/auth").startsWith("/") ? String(path || "/auth") : `/${path}`;
  const normalizedReturnPath = normalizeAuthReturnPath(returnPath);
  if (!normalizedReturnPath) {
    return normalizedPath;
  }
  const url = new URL(normalizedPath, "https://min.cool");
  url.searchParams.set(AUTH_RETURN_QUERY_PARAM_KEY, normalizedReturnPath);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function getOnboardingEmailRedirectUrl(path = "/auth", options = {}) {
  if (typeof window === "undefined" || !window.location?.origin) {
    return "";
  }
  const normalizedPath = String(path || "/auth").startsWith("/") ? String(path || "/auth") : `/${path}`;
  const url = new URL(normalizedPath, window.location.origin);
  const handoffToken = String(options?.handoffToken || "").trim();
  if (handoffToken) {
    url.searchParams.set(HANDOFF_QUERY_PARAM_KEY, handoffToken);
  }
  return url.toString();
}

function buildOnboardingAuthDraftKey(contextKey = "") {
  const normalizedContextKey = String(contextKey || "").trim();
  return normalizedContextKey
    ? `${AUTH_DRAFT_KEY_PREFIX}.${normalizedContextKey}`
    : AUTH_DRAFT_KEY_PREFIX;
}

function getStorage() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage || window.sessionStorage;
}

function getStorageTargets() {
  if (typeof window === "undefined") {
    return [];
  }
  return [window.localStorage, window.sessionStorage].filter(Boolean);
}

function readStoredJson(key) {
  for (const store of getStorageTargets()) {
    const raw = store.getItem(key);
    if (!raw) {
      continue;
    }
    try {
      return JSON.parse(raw);
    } catch {
      store.removeItem(key);
    }
  }
  return null;
}

function writeStoredJson(key, value) {
  const encoded = JSON.stringify(value);
  for (const store of getStorageTargets()) {
    store.setItem(key, encoded);
  }
}

function clearStoredValue(key) {
  for (const store of getStorageTargets()) {
    store.removeItem(key);
  }
}

function clearStoredValuesWithPrefix(prefix = "") {
  const normalizedPrefix = String(prefix || "").trim();
  if (!normalizedPrefix) {
    return;
  }
  for (const store of getStorageTargets()) {
    const keysToClear = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (String(key || "").startsWith(normalizedPrefix)) {
        keysToClear.push(key);
      }
    }
    for (const key of keysToClear) {
      store.removeItem(key);
    }
  }
}

export function readOnboardingAuthDraft(contextKey = "") {
  return normalizeOnboardingAuthState(readStoredJson(buildOnboardingAuthDraftKey(contextKey)) || {});
}

export function readOnboardingAuthDraftUserMode(contextKey = "") {
  const normalizedUserMode = String((readStoredJson(buildOnboardingAuthDraftKey(contextKey)) || {}).userMode || "").trim().toLowerCase();
  return USER_MODE_VALUES.includes(normalizedUserMode) ? normalizedUserMode : "";
}
export function storeOnboardingAuthDraft(partial = {}, contextKey = "") {
  const next = normalizeOnboardingAuthState(partial);
  writeStoredJson(buildOnboardingAuthDraftKey(contextKey), next);
  return next;
}

export function clearOnboardingAuthDraft(contextKey = "") {
  clearStoredValue(buildOnboardingAuthDraftKey(contextKey));
}

export function clearAllOnboardingAuthDrafts() {
  clearStoredValuesWithPrefix(AUTH_DRAFT_KEY_PREFIX);
}

export function storeOnboardingHandoffToken(token) {
  if (!token) {
    return;
  }
  const normalizedToken = String(token);
  for (const store of getStorageTargets()) {
    store.setItem(HANDOFF_TOKEN_KEY, normalizedToken);
  }
  try {
    window[CURRENT_PAGE_HANDOFF_TOKEN_KEY] = normalizedToken;
  } catch {
    // Window-like test doubles can still use browser storage.
  }
}

export function readOnboardingHandoffToken() {
  const urlToken = readOnboardingHandoffTokenFromUrl();
  if (urlToken) {
    storeOnboardingHandoffToken(urlToken);
    return urlToken;
  }
  const storage = getStorage();
  return storage ? storage.getItem(HANDOFF_TOKEN_KEY) : null;
}
export function clearOnboardingHandoffToken() {
  clearStoredValue(HANDOFF_TOKEN_KEY);
  try { delete window[CURRENT_PAGE_HANDOFF_TOKEN_KEY]; } catch { /* Storage-only environments can ignore page cleanup. */ }
}
